import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Chip, Divider, CircularProgress, Stack, Snackbar, Alert,
   Tooltip, Button, useTheme, Dialog, DialogTitle, IconButton, TextField,
   DialogActions, Avatar, Card, CardContent, Select, MenuItem, FormControl, InputLabel, Pagination } from '@mui/material';
import { WorkOutline, LocationOn, ContactMail, Paid, Category, DownloadOutlined, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import { fetchCompanyHiringPosts } from '../../../helper/organizationsApis';
import { generateHiringPDF } from './generateHiringPDF';
import EditHiringPostDialog from './EditHiringPostDialog';
import { OrganizationHiretechupadate, OrganizationHiretechfeedback } from '../../../helper/base_url';
import AppliedUsersDialog from './AppliedUsersDialog';

const ITEMS_PER_PAGE = 2;
const opportunityTypes = ['Internship', 'Full-time Job'];

const CompanyHiringPosts = () => {
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [appliedUsersOpen, setAppliedUsersOpen] = useState(false);
  const [selectedAppliedUsers, setSelectedAppliedUsers] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedOpportunityType, setSelectedOpportunityType] = useState('Internship');
  const [page, setPage] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const theme = useTheme();
  //const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchHiringPosts = async () => {
    try {
      const data = await fetchCompanyHiringPosts();
      setCompanyData(data);
    } catch (error) {
      //console.error('Error fetching company hiring posts:', error);
      setErrorSnackbarOpen(true);
      setErrorMessage('Failed to fetch hiring posts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditingPost(null);
    setEditOpen(false);
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('company_token');
      await axios.delete(`${OrganizationHiretechupadate}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { hiringId: deleteTarget._id },
      });
      fetchHiringPosts();
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleFeedbackChange = (index, value) => {
    const updatedHirings = [...companyData.hirings];
    updatedHirings[index].feedback = value;
    setCompanyData({ ...companyData, hirings: updatedHirings });
  };

  const handleFeedbackSubmit = async (hiringId, feedback) => {
    try {
      const token = localStorage.getItem('company_token');
      await axios.put(
        `${OrganizationHiretechfeedback}`,
        { hiringId, feedback },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSnackbarOpen(true);
      fetchHiringPosts();
    } catch (error) {
      //console.error('Error submitting feedback:', error);
      setErrorMessage(error.response?.data?.error || 'Something went wrong!');
      setErrorSnackbarOpen(true);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    fetchHiringPosts();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!companyData) {
    return (
    <Box textAlign="center" py={10}>
      <Typography variant="h6" color="text.secondary">
        No internships or jobs yet.
      </Typography>
      <Typography variant="body2" mt={1}>
        Add a new opportunity to get started.
      </Typography>
    </Box>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Accepted':
        return 'success';
      case 'Rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  // Filter and paginate posts
  const filteredPosts = companyData.hirings.filter(
    (post) => post.opportunityOverview.opportunityType === selectedOpportunityType
  );

  const paginatedPosts = filteredPosts.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  return (
    <Box
      sx={{
        inlineSize: '100%',
        backgroundColor: '#f4f6f8',
        padding: '110px 20px 20px 20px',
        blockSize: '100%',
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      {/* Filter Dropdown */}
      <Box mb={4} maxWidth={200}>
        <FormControl fullWidth size="small">
          <InputLabel id="opportunity-type-label">Opportunity Type</InputLabel>
          <Select
            labelId="opportunity-type-label"
            value={selectedOpportunityType}
            label="Opportunity Type"
            onChange={(e) => {
              setSelectedOpportunityType(e.target.value);
              setPage(1);
            }}
          >
            {opportunityTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={4}>
        {paginatedPosts.length === 0 ? (
          <Grid item xs={12}>
            <Typography textAlign="center" color="text.secondary">
              No {selectedOpportunityType} opportunities found.
            </Typography>
          </Grid>
        ) : (
          paginatedPosts.map((post, index) => {
            const {
              opportunityOverview: overview,
              roleDetails: role,
              compensationAndDates: comp,
              contactInformation: contact,
            } = post;
            const overallStatus = post.overallStatus || 'Pending';

            return (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={3}
                  sx={{
                    borderRadius: 3,
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'scale(1.02)' },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Chip label={overallStatus} color={getStatusColor(overallStatus)} />
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleEdit(post)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => setDeleteTarget(post)}>
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                      <Avatar sx={{ bgcolor: '#052659' }}>
                        <WorkOutline />
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{overview.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Category fontSize="small" sx={{ mr: 0.5 }} />
                          {overview.opportunityType} |{' '}
                          {Array.isArray(role.domain) ? role.domain.join(', ') : role.domain}
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                      <Chip icon={<LocationOn />} label={overview.location} size="small" />
                      <Chip icon={<Paid />} label={comp.stipendOrSalary || '-'} size="small" />
                      <Chip label={`Exp: ${overview.experience}`} size="small" />
                    </Stack>

                    <Typography variant="subtitle2" mt={2}>
                      Duration: {overview.duration}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Deadline: {comp.applicationDeadline?.slice(0, 10)}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" mt={1}>
                      <ContactMail fontSize="small" sx={{ mr: 0.5 }} />
                      {contact.contactName} | {contact.contactEmail}
                    </Typography>

                    <Box mt={2}>
                      <Typography variant="subtitle2">Role Overview</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {role.roleOverview}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
                      {role.skills?.map((skill, i) => (
                        <Chip
                          key={i}
                          label={skill}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{
                            bgcolor: '#e3f2fd',
                            border: 'none',
                            borderRadius: 2,
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Stack>

                    <Stack direction="row" spacing={2} mt={3} flexWrap="wrap" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setSelectedAppliedUsers(post.appliedUsers || []);
                          setSelectedPost(post);
                          setAppliedUsersOpen(true);
                        }}
                      >
                        Applied Users ({post.appliedUsers?.length || 0})
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadOutlined />}
                        onClick={() => generateHiringPDF(post, companyData)}
                      >
                        PDF
                      </Button>
                    </Stack>

                       {/* Feedback Section */}
                    <Box mt={3}>
                      <Typography variant="subtitle2">Feedback</Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          placeholder="Write feedback"
                          value={post.feedback || ''}
                          onChange={(e) => handleFeedbackChange(index, e.target.value)}
                          fullWidth
                        />
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleFeedbackSubmit(post._id, post.feedback)}
                        >
                          Save
                        </Button>
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>
      {/* Snackbar for success messages */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          Feedback submitted successfully!
        </Alert>
      </Snackbar>
      {/* Snackbar for error messages */}
      <Snackbar open={errorSnackbarOpen} autoHideDuration={6000} onClose={() => setErrorSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
      {/* Edit Dialog */}
      <EditHiringPostDialog open={editOpen} onClose={handleEditClose} post={editingPost} onPostUpdated={fetchHiringPosts} />

      {/* Applied Users Dialog */}
      <AppliedUsersDialog
        open={appliedUsersOpen}
        onClose={() => setAppliedUsersOpen(false)}
        selectedAppliedUsers={selectedAppliedUsers}
        selectedPost={selectedPost}
      />

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <Box px={3} py={1}>
          <Typography>Are you sure you want to delete "{deleteTarget?.opportunityOverview?.title}"?</Typography>
        </Box>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
       {/* Pagination */}
      {filteredPosts.length > ITEMS_PER_PAGE && (
        <Box mt={5} display="flex" justifyContent="center">
          <Pagination
            count={Math.ceil(filteredPosts.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default CompanyHiringPosts;
